import { Location } from "../types/location";

/**
 * Formats a location display name, including the parent location (partOf) if it exists.
 * Format: "${partOf.display}: ${name}" if partOf exists, otherwise just "${name}"
 *
 * @param location - The location object to format
 * @returns The formatted display name string
 */
export const getLocationDisplayName = (location: Location): string => {
  if (!location?.name) return "";
  if (location.partOf?.display) {
    return `${location.partOf.display}: ${location.name}`;
  }
  return location.name;
};

export const shouldDisableLocation = (location: Location): boolean => {
  return (
    ["suspended", "inactive"].includes(location.status) ||
    (location.operationalStatus?.code &&
      location.operationalStatus?.code !== "U")
  );
};

export const generateLocationOptionDisplay = (location: Location): string => {
  return `${getLocationDisplayName(location)} ${location.alias?.join(" ")}`;
};

export const LocationConfig = {
  locationSchedulingPolicy: {
    defaults: {
      schedulable: false,
      capacity: null,
      capacityMode: "none",
      countStatuses: ["booked", "arrived", "in-progress"],
      excludeStatuses: ["cancelled", "noshow"],
      bufferBeforeMinutes: 0,
      bufferAfterMinutes: 0,
      allowOverbook: false,
      requiresOverrideReason: true,
      rollupToParentsForReporting: true,
      enforceParentCapacity: false,
    },
    byCode: {
      si: {
        schedulable: false,
        capacityMode: "soft",
        softThresholdPerHour: null,
      },
      bu: {
        schedulable: false,
        capacityMode: "soft",
        softThresholdPerHour: null,
      },
      wi: {
        schedulable: false,
        capacityMode: "soft",
        softThresholdPerHour: null,
      },

      // Ward is a container by default. Enable schedulable only for ward-round/group events.
      wa: {
        schedulable: false,
        capacityMode: "soft",
      },

      lvl: { schedulable: false, capacityMode: "soft" },
      co: { schedulable: false, capacityMode: "none" },

      ro: {
        schedulable: true,
        capacity: 1,
        capacityMode: "hard",
        bufferBeforeMinutes: 0,
        bufferAfterMinutes: 10,
        allowOverbook: true,
        requiresOverrideReason: true,
      },

      // Bed scheduling is typically encounter/admission-driven; hard-cap if used for procedures/reservations.
      bd: {
        schedulable: true,
        capacity: 1,
        capacityMode: "hard",
        bufferBeforeMinutes: 0,
        bufferAfterMinutes: 0,
        allowOverbook: true,
        requiresOverrideReason: true,
      },
      // If vehicle is seat-based transport, model seat capacity explicitly.
      ve: {
        schedulable: true,
        capacity: 1,
        capacityMode: "hard",
        bufferBeforeMinutes: 0,
        bufferAfterMinutes: 15,
        allowOverbook: true,
        requiresOverrideReason: true,
      },

      // If home visits are needed, schedule against provider schedule; optionally enable schedulable with hard cap=1.
      ho: {
        schedulable: false,
        capacityMode: "none",
      },

      // Cabinet is storage by default. If used as a service window/station, set servicePoint=true and treat like room.
      ca: {
        schedulable: false,
        capacityMode: "none",
      },

      rd: { schedulable: false, capacityMode: "none" },
      area: { schedulable: false, capacityMode: "soft" },

      // Geographic concept; aligns to HealthcareService.coverageArea, not physical appointment scheduling.
      jdn: {
        schedulable: false,
        capacityMode: "none",
      },

      // Virtual capacity should be enforced via provider availability, not location.
      vi: {
        schedulable: true,
        capacity: 9999,
        capacityMode: "soft",
        allowOverbook: true,
        requiresOverrideReason: false,
      },
    },
    flags: {
      treatCabinetAsServicePointWhen: {
        field: "servicePoint",
        equals: true,
        override: {
          schedulable: true,
          capacity: 1,
          capacityMode: "hard",
          bufferAfterMinutes: 5,
        },
      },
      enableWardSchedulingWhen: {
        field: "wardSchedulingEnabled",
        equals: true,
        override: { schedulable: true, capacity: 20, capacityMode: "soft" },
      },
      enableHouseSchedulingWhen: {
        field: "homeVisitEnabled",
        equals: true,
        override: { schedulable: true, capacity: 1, capacityMode: "hard" },
      },
    },
  },
};
